// ---------- la file des paliers à célébrer ----------
// Ce que le joueur a gagné depuis la dernière fois qu'on le lui a montré, mis en cartes. Pur : ni
// DOM, ni stockage, ni réseau — src/celebrate.ts s'en charge, et ce module se teste seul
// (tests/badgeQueue.test.ts).
//
// POURQUOI UN RELEVÉ « DÉJÀ VU » SUR L'APPAREIL. `GET /me/badges` renvoie un ÉTAT — le meilleur
// palier de chaque badge — et pas des événements « palier gagné ». Le nouveau, c'est l'écart
// entre cet état et ce que l'appareil a déjà montré. Le faire côté jeu laisse la route telle
// quelle, et le serveur n'a rien à apprendre pour une fonction que les anciens APK ignorent.
//
// Les règles viennent de la maquette validée (artefact « Badge gagné Codigrid ») :
//   - une carte par badge, au palier le plus haut — bronze, argent et or d'un coup font UNE carte
//     « Or », et les paliers franchis au passage se lisent dessous ;
//   - trois cartes au plus, les paliers les plus précieux d'abord ; le reste tient sur une
//     carte « Et aussi » ;
//   - le premier relevé d'un compte qui a déjà un historique ne déroule pas quinze cartes : il
//     est pris comme déjà vu, et une seule carte d'accueil le résume.

import type { BadgeState, BadgeTier } from './api';

export const TIERS: readonly BadgeTier[] = ['bronze', 'argent', 'or', 'platine', 'prisme'];

/** Au-delà, les badges restants passent sur la carte « Et aussi ». */
export const MAX_CARDS = 3;

/**
 * Le seuil de la carte d'accueil, en paliers. Ce qui tient dans une file ordinaire (trois paliers
 * au plus, donc trois cartes au plus) s'y déroule normalement : c'est le cas du joueur tout neuf
 * dont le premier relevé suit sa première feuille, et lui accueillir « tes badges sont arrivés »
 * pour un seul bronze sonnerait faux. Au-dessus, c'est un historique — celui d'avant la mise à
 * jour, ou d'un compte retrouvé par un code de reprise.
 */
export const WELCOME_FROM = MAX_CARDS + 1;

/** La carte d'accueil montre au plus deux rangées de trois médailles. Trois et non quatre par
 *  rangée : à quatre, « Bibliothèque » ne tenait pas sous sa médaille et se coupait en plein mot
 *  (vu à 412 px de large). Deux rangées et non trois : la carte doit tenir sur un petit écran. */
export const WELCOME_MEDALS = 6;

/** Ce que l'appareil a déjà montré. `ranks[id]` est l'indice du palier dans TIERS ; un badge
 *  absent n'a rien montré (−1). */
export interface Seen {
  /** Une empreinte du jeton du compte — jamais le jeton lui-même. Un autre compte sur le même
   *  appareil (code de reprise, compte supprimé puis recréé) repart d'un premier relevé. */
  owner: string;
  ranks: Record<string, number>;
  /** La carte d'accueil n'a pas encore été vue ; le nombre de paliers qu'elle annonce. */
  welcome?: number;
}

export interface Gain {
  badge: BadgeState;
  /** Le palier montré jusqu'ici, −1 si aucun. Ceux entre `from` et le palier actuel sont les
   *  paliers franchis au passage. */
  from: number;
}

export type Card =
  | { kind: 'medal'; gain: Gain }
  | { kind: 'more'; gains: Gain[]; tiers: number }
  | { kind: 'welcome'; badges: BadgeState[]; tiers: number };

/** Une empreinte du jeton, pour reconnaître le compte sans garder le jeton une seconde fois.
 *  Partagée par le relevé « déjà vu » (src/celebrate.ts) et le cache de l'écran « Moi »
 *  (src/profile.ts) : les deux doivent oublier ce qui appartient à un autre compte — code de
 *  reprise échangé, compte supprimé puis recréé, jeton re-créé sur un 401. */
export function fingerprint(token: string): string {
  let h = 2166136261;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function rankOf(b: BadgeState): number {
  return b.tier ? TIERS.indexOf(b.tier) : -1;
}

/** Les paliers sautés d'un gain : ni celui d'avant, ni celui qu'on célèbre. */
export function skippedTiers(g: Gain): BadgeTier[] {
  return TIERS.slice(g.from + 1, rankOf(g.badge));
}

/** Le premier relevé d'un compte sur cet appareil. */
export function firstLook(owner: string, badges: readonly BadgeState[]): Seen {
  let tiers = 0;
  for (const b of badges) tiers += rankOf(b) + 1;
  if (tiers < WELCOME_FROM) return { owner, ranks: {} };
  const ranks: Record<string, number> = {};
  for (const b of badges) ranks[b.id] = rankOf(b);
  return { owner, ranks, welcome: tiers };
}

export function buildQueue(badges: readonly BadgeState[], seen: Seen): Card[] {
  const cards: Card[] = [];
  if (seen.welcome) {
    const won = badges.filter((b) => rankOf(b) >= 0);
    // Tri stable : à palier égal, l'ordre du serveur (jalons, familles, catégories) reste.
    won.sort((a, b) => rankOf(b) - rankOf(a));
    cards.push({ kind: 'welcome', badges: won.slice(0, WELCOME_MEDALS), tiers: seen.welcome });
  }
  const gains: Gain[] = badges
    .map((badge) => ({ badge, from: seen.ranks[badge.id] ?? -1 }))
    .filter((g) => rankOf(g.badge) > g.from);
  gains.sort((a, b) => rankOf(b.badge) - rankOf(a.badge));
  if (gains.length <= MAX_CARDS) {
    for (const gain of gains) cards.push({ kind: 'medal', gain });
    return cards;
  }
  for (const gain of gains.slice(0, MAX_CARDS)) cards.push({ kind: 'medal', gain });
  const rest = gains.slice(MAX_CARDS);
  let tiers = 0;
  for (const g of rest) tiers += rankOf(g.badge) - g.from;
  cards.push({ kind: 'more', gains: rest, tiers });
  return cards;
}

/** Le relevé après qu'une carte a été vue. Un palier montré ne redescend jamais, même si le
 *  serveur en renvoyait un plus bas : il ne doit pas être célébré une seconde fois. */
export function markSeen(seen: Seen, card: Card): Seen {
  const next: Seen = { ...seen, ranks: { ...seen.ranks } };
  const note = (g: Gain): void => {
    next.ranks[g.badge.id] = Math.max(next.ranks[g.badge.id] ?? -1, rankOf(g.badge));
  };
  if (card.kind === 'medal') note(card.gain);
  else if (card.kind === 'more') card.gains.forEach(note);
  else delete next.welcome;
  return next;
}
